import { create } from 'zustand'

/**
 * The booking follow-up's own store (PRODUCT-525): the openings waiting to
 * be shown, as PLAIN DATA — a key and the created ticket ids per opening,
 * nothing else. `BookingFollowUpHost` renders the dialog for each entry.
 *
 * Why data and not dialogs (PRODUCT-545): the door used to put the
 * `React.lazy` dialog ELEMENT into the app's async-modal store through
 * immer's `produce`. immer deep-freezes whatever it stores, and React
 * initialises a lazy component by writing `_status` on its payload the first
 * time it renders — so the production build threw "Cannot assign to read
 * only property '_status'" on every booking. Development never saw it:
 * React itself freezes elements there, and immer stops at anything already
 * frozen. Nothing React mutates may live in this store — no elements,
 * components or functions — and nothing here is written through immer.
 */
export type TBookingFollowUpEntry = {
  /** One per opening — the host's React key and the handle a close uses. */
  key: string
  /**
   * Every ticket the booking created, the primary (first) unit first — the
   * one "Open ticket" lands on. Scheduling puts all of them on the route.
   */
  ticketIds: string[]
}

export type TBookingFollowUpState = {
  /** The openings not yet dismissed, oldest first. */
  entries: TBookingFollowUpEntry[]
}

export const bookingFollowUpStore = create<TBookingFollowUpState>(() => ({
  entries: [],
}))

let sequence = 0

/** Queue an opening for the host to show; answers its key. */
export const pushBookingFollowUp = (ticketIds: readonly string[]): string => {
  sequence += 1
  const key = `booking-follow-up-${sequence}`
  bookingFollowUpStore.setState((state) => ({
    entries: [...state.entries, { key, ticketIds: [...ticketIds] }],
  }))
  return key
}

/** Take an opening down — Later, Open ticket, the X, a finished schedule. */
export const dismissBookingFollowUp = (key: string): void => {
  bookingFollowUpStore.setState((state) => ({
    entries: state.entries.filter((entry) => entry.key !== key),
  }))
}

/** Tests: back to an empty store. */
export const resetBookingFollowUpStore = (): void => {
  bookingFollowUpStore.setState({ entries: [] })
}
